import React from 'react';
import { Box, StatusIndicator } from '@cloudscape-design/components';
import { CopilotMessage, useCopilot } from './CopilotContext';
import { CopilotMarkdown } from './CopilotMarkdown';
import { CopilotReasoning } from './CopilotReasoning';
import { ASTRA_ICON_URL } from './brand';
import { formatMessageTime } from 'src/utils/dateUtils';

interface CopilotMessageBubbleProps {
  message: CopilotMessage;
}

function getUserInitials(userId: string): string {
  if (!userId) return '?';
  return userId.slice(0, 2).toUpperCase();
}

/** Animated placeholder shown while an agent turn is streaming but has no answer text yet. */
function TypingIndicator() {
  return (
    <span className="copilot-typing" role="status" aria-label="Astra is responding">
      <span className="copilot-typing-dot" />
      <span className="copilot-typing-dot" />
      <span className="copilot-typing-dot" />
    </span>
  );
}

export function CopilotMessageBubble({ message }: CopilotMessageBubbleProps) {
  const { userId } = useCopilot();
  const isUser = message.role === 'user';
  const isAgent = !isUser && !message.isError;

  const hasContent = message.content.length > 0;
  const hasReasoning = !!message.reasoning;
  // While streaming with no reasoning trace yet (e.g. stages with thinking disabled),
  // the bubble carries the "responding" indicator; otherwise the reasoning section does.
  const showTyping = isAgent && message.isStreaming && !hasContent && !hasReasoning;
  const showBubble = !isAgent || hasContent || showTyping;

  return (
    <div
      className={`copilot-message-row ${isUser ? 'copilot-message-row--user' : 'copilot-message-row--agent'}`}
    >
      {!isUser && (
        <div
          className="copilot-message-avatar copilot-message-avatar--agent"
          role="img"
          aria-label="Astra"
        >
          <img className="copilot-message-avatar-img" src={ASTRA_ICON_URL} alt="" />
        </div>
      )}
      <div className="copilot-message-content">
        {isAgent && hasReasoning && (
          <CopilotReasoning
            reasoning={message.reasoning ?? ''}
            active={!!message.isStreaming && !hasContent}
          />
        )}
        {showBubble && (
          <div
            className={`copilot-message-bubble ${
              isUser
                ? 'copilot-message-bubble--user'
                : message.isError
                  ? 'copilot-message-bubble--error'
                  : 'copilot-message-bubble--agent'
            }`}
          >
            {isUser ? (
              <Box variant="p" color="inherit">
                {message.content}
              </Box>
            ) : message.isError ? (
              <StatusIndicator type="error">{message.content}</StatusIndicator>
            ) : hasContent ? (
              <CopilotMarkdown content={message.content} />
            ) : (
              <TypingIndicator />
            )}
          </div>
        )}
        {isAgent && message.interrupted && (
          <span className="copilot-message-interrupted">
            <StatusIndicator type="warning">
              Response was interrupted. Please try again.
            </StatusIndicator>
          </span>
        )}
        <span className="copilot-message-timestamp">
          {formatMessageTime(message.timestamp)}
        </span>
      </div>
      {isUser && (
        <div
          className="copilot-message-avatar copilot-message-avatar--user"
          role="img"
          aria-label="You"
        >
          {getUserInitials(userId)}
        </div>
      )}
    </div>
  );
}
